import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LangProvider } from './i18n';
import Header from './components/Header';
import Footer from './components/Footer';
import SplashScreen from './components/SplashScreen';
import RegistrationPopup from './components/RegistrationPopup';
import Home from './pages/Home';
import CityPage from './pages/CityPage';
import CategoriesGrid from './pages/CategoriesGrid';
import CategoryDetail from './pages/CategoryDetail';
import Story from './pages/Story';
import Emergency from './pages/Emergency';
import Download from './pages/Download';
import DesenzanoLoft from './pages/DesenzanoLoft';
import FreeVersion from './pages/FreeVersion';
import SearchResults from './pages/SearchResults';
import Nearby from './pages/Nearby';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import CookieBanner from './components/CookieBanner';
import { RadioPlayerProvider } from './radio';
import { RadioMiniPlayer, RadioFullModal } from './components/RadioPlayer';
import { useLang } from './i18n';
// Admin
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCategories from './pages/admin/AdminCategories';
import AdminSubcategories from './pages/admin/AdminSubcategories';
import AdminActivities from './pages/admin/AdminActivities';
import AdminBanners from './pages/admin/AdminBanners';
import AdminFlashOffers from './pages/admin/AdminFlashOffers';
import AdminItineraries from './pages/admin/AdminItineraries';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminExperiences from './pages/admin/AdminExperiences';
import AdminPartnerLeads from './pages/admin/AdminPartnerLeads';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminCities from './pages/admin/AdminCities';
import AdminEmergency from './pages/admin/AdminEmergency';
import AdminStories from './pages/admin/AdminStories';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminImport from './pages/admin/AdminImport';
import AdminSponsorUpgrades from './pages/admin/AdminSponsorUpgrades';
import AdminModerationLogs from './pages/admin/AdminModerationLogs';
import AdminBrokenImages from './pages/admin/AdminBrokenImages';
import AdminQrPages from './pages/admin/AdminQrPages';
import Itinerary from './pages/Itinerary';
import Experiences from './pages/Experiences';
import ExperienceDetail from './pages/ExperienceDetail';
import PromoteBusiness from './pages/PromoteBusiness';
import Favorites from './pages/Favorites';
import ZonePage from './pages/ZonePage';
import AdminSponsorUsers from './pages/admin/AdminSponsorUsers';
import AdminCurrency from './pages/admin/AdminCurrency';
import SponsorLogin from './pages/sponsor/SponsorLogin';
import SponsorDashboard from './pages/sponsor/SponsorDashboard';
import { CurrencyProvider } from './currency';
import { FavoritesProvider } from './favorites';
import OfflineBanner, { registerServiceWorker } from './components/OfflineBanner';
import MobileBackButton from './components/MobileBackButton';

registerServiceWorker();

// Wrapper to render Header/Footer/Chat only on public routes
function PublicShell({ children }) {
  const loc = useLocation();
  const { lang } = useLang();
  const isAdmin = loc.pathname.startsWith('/admin') || loc.pathname.startsWith('/sponsor');
  // Standalone QR-code landing pages: no header/footer/chat.
  const isStandalone =
    loc.pathname === '/download' ||
    loc.pathname === '/desenzanoloft' ||
    loc.pathname.startsWith('/freeversion/');
  if (isAdmin) return <div className="App app-admin">{children}</div>;
  if (isStandalone) return <div className="App">{children}</div>;
  return (
    <div className="App">
      <OfflineBanner />
      <Header />
      <MobileBackButton />
      <main>{children}</main>
      <Footer />
      <CookieBanner lang={lang} />
      <RadioMiniPlayer />
      <RadioFullModal />
    </div>
  );
}

function App() {
  return (
    <LangProvider>
      <CurrencyProvider>
        <FavoritesProvider>
          <RadioPlayerProvider>
          <BrowserRouter>
            <SplashScreen />
            <RegistrationPopup />
            <PublicShell>
              <Routes>
                  {/* Admin */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="cities" element={<AdminCities />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="subcategories" element={<AdminSubcategories />} />
                    <Route path="activities" element={<AdminActivities />} />
                    <Route path="banners" element={<AdminBanners />} />
                    <Route path="flash-offers" element={<AdminFlashOffers />} />
                    <Route path="itineraries" element={<AdminItineraries />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="experiences" element={<AdminExperiences />} />
                    <Route path="partner-leads" element={<AdminPartnerLeads />} />
                    <Route path="notifications" element={<AdminNotifications />} />
                    <Route path="sponsor-users" element={<AdminSponsorUsers />} />
                    <Route path="currency" element={<AdminCurrency />} />
                    <Route path="stories" element={<AdminStories />} />
                    <Route path="emergency" element={<AdminEmergency />} />
                    <Route path="newsletter" element={<AdminNewsletter />} />
                    <Route path="import" element={<AdminImport />} />
                    <Route path="sponsor-upgrades" element={<AdminSponsorUpgrades />} />
                    <Route path="moderation-logs" element={<AdminModerationLogs />} />
                    <Route path="broken-images" element={<AdminBrokenImages />} />
                    <Route path="qr-pages" element={<AdminQrPages />} />
                  </Route>

                  {/* Sponsor portal */}
                  <Route path="/sponsor/login" element={<SponsorLogin />} />
                  <Route path="/sponsor" element={<SponsorDashboard />} />

                  {/* Public */}
                  <Route path="/" element={<Home />} />
                  <Route path="/preferiti" element={<Favorites />} />
                  <Route path="/download" element={<Download />} />
                  <Route path="/desenzanoloft" element={<DesenzanoLoft />} />
                  <Route path="/freeversion/:slug" element={<FreeVersion />} />
                  <Route path="/cerca" element={<SearchResults />} />
                  <Route path="/vicino-a-me" element={<Nearby />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/promuoviti" element={<PromoteBusiness />} />
                  <Route path="/itinerari/:slug" element={<Itinerary />} />
                  <Route path="/zone/:slug" element={<ZonePage />} />
                  <Route path="/desenzano" element={<CityPage />} />
                  <Route path="/sirmione" element={<CityPage />} />
                  <Route path="/peschiera" element={<CityPage />} />
                  {/* Legacy Wix URLs */}
                  <Route path="/desenzanonew" element={<Navigate to="/desenzano" replace />} />
                  <Route path="/copia-di-sirmione" element={<Navigate to="/peschiera" replace />} />
                  <Route path="/puntidiinteresse" element={<Navigate to="/desenzano/cosa-vedere" replace />} />
                  <Route path="/pointsirmione" element={<Navigate to="/sirmione/cosa-vedere" replace />} />
                  <Route path="/storiadesenzano" element={<Navigate to="/desenzano/storia" replace />} />
                  <Route path="/storiasirmione" element={<Navigate to="/sirmione/storia" replace />} />
                  <Route path="/emergenze" element={<Navigate to="/desenzano/emergenze" replace />} />
                  {/* Dynamic city sub-pages */}
                  <Route path="/:slug/experiences" element={<Experiences />} />
                  <Route path="/:slug/experiences/:expSlug" element={<ExperienceDetail />} />
                  <Route path="/:slug/cosa-vedere" element={<CategoriesGrid />} />
                  <Route path="/:slug/cosa-vedere/:cat" element={<CategoryDetail />} />
                  <Route path="/:slug/cosa-vedere/:cat/:sub" element={<CategoryDetail />} />
                  <Route path="/:slug/storia" element={<Story />} />
                  <Route path="/:slug/emergenze" element={<Emergency />} />
                  {/* Generic city page — must come AFTER all more-specific routes
                      so /admin, /preferiti, /cerca, etc. still match their static
                      routes. Any other single-segment path resolves to CityPage,
                      which returns 404 gracefully if the slug is unknown. */}
                  <Route path="/:slug" element={<CityPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PublicShell>
            </BrowserRouter>
          </RadioPlayerProvider>
        </FavoritesProvider>
      </CurrencyProvider>
    </LangProvider>
  );
}

export default App;
